import React, { useEffect, useReducer } from 'react';
import { DefaultTheme, useTheme } from 'styled-components';
import { CallToAction } from '../../../commonTypes/links';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { generateKeyForElement } from '../../../helpers/elementKeyHelper';
import { applyComponentTheme } from '../../../theme';
import { CardColumnSpanCalculator } from '../../CardGrid';
import Collapsible from '../../Collapsible';
import { SecondaryCta } from '../../Cta';
import { Grid, GridItem } from '../../Grid';
import RelatedContentCard, { RelatedContentCardProps } from '../RelatedContentCard';
import { CtaGrid, Heading } from '../styles';
import { CollapsibleCardGrid, CtaWrapper, InitialDisplayCardGrid } from './styles';

export const columnSpanCalculator: (theme: DefaultTheme) => CardColumnSpanCalculator = (
  theme: DefaultTheme
) => () => ({
  // mobile cards are always full-width
  tabletPortrait: theme.layout.tabletPortrait.columns / 2,
  // On desktop, cards take up a third of the remaining space.
  // The grid should be 9 cols wide so each item will have width 3
  tabletLandscape: theme.layout.tabletLandscape.columns / 3,
  desktop: theme.layout.desktop.columns / 3,
});

const defaultShowMoreCta: CallToAction = {
  displayText: 'Show more cards',
  screenReaderText: 'Show more cards',
};
const defaultHideCta: CallToAction = {
  displayText: 'Hide cards',
  screenReaderText: 'Hide cards',
};

export type RelatedContentWithVisibilityToggleProps = {
  heading?: string;
  cardProps: RelatedContentCardProps[];
  ctas?: {
    showMore: CallToAction;
    hide: CallToAction;
  };
} & ComponentProps;

const RelatedContentWithVisibilityToggle: React.FC<RelatedContentWithVisibilityToggleProps> = ({
  heading,
  cardProps,
  ctas,
  ...props
}) => {
  const [showMoreCards, toggleShowMoreCards] = useReducer((state) => !state, false);
  // The animation causes issues with react-truncate and flexbox styles, so we force an update
  // on the CollapsibleCardGrid to ensure that the truncation and styling is correct
  const [rerenderKey, triggerRerender] = useReducer((i) => i + 1, 0);
  useEffect(() => {
    if (showMoreCards) {
      triggerRerender();
    }
  }, [showMoreCards]);
  const theme = useTheme();

  const showMoreCta = {
    ...defaultShowMoreCta,
    ...(ctas?.showMore || {}),
    url: undefined,
    onClick: toggleShowMoreCards,
  };
  const hideCta = {
    ...defaultHideCta,
    ...(ctas?.hide || {}),
    url: undefined,
    onClick: toggleShowMoreCards,
  };

  return (
    <div {...componentProps(props)}>
      {heading && (
        <Grid>
          <GridItem>
            <Heading>{heading}</Heading>
          </GridItem>
        </Grid>
      )}
      <InitialDisplayCardGrid
        columnSpanCalculator={columnSpanCalculator(theme)}
        alignLeft>
        {cardProps.slice(0, 4).map((card) => (
          <RelatedContentCard key={generateKeyForElement(card)} {...card} />
        ))}
      </InitialDisplayCardGrid>
      <Collapsible isOpened={showMoreCards}>
        <CollapsibleCardGrid
          key={rerenderKey}
          columnSpanCalculator={columnSpanCalculator(theme)}
          alignLeft>
          {/* We have the 4th card duplicated in both CardGrids and use CSS to hide depending on screen size,
              because on desktop we display 3 initially but on tablet and mobile we display 4 initially */}
          {cardProps.slice(3).map((card) => (
            <RelatedContentCard key={generateKeyForElement(card)} {...card} />
          ))}
        </CollapsibleCardGrid>
      </Collapsible>
      <CtaGrid>
        <GridItem>
          <CtaWrapper
            isVisibleDesktop={cardProps.length > 3}
            isVisibleMobile={cardProps.length > 4}>
            <SecondaryCta cta={showMoreCards ? hideCta : showMoreCta} />
          </CtaWrapper>
        </GridItem>
      </CtaGrid>
    </div>
  );
};

export default applyComponentTheme(
  RelatedContentWithVisibilityToggle,
  'relatedContentWithVisibilityToggle'
);
